/**
 * Indian currency formatting utilities.
 * Rs 2.5 Cr, Rs 45 Lakh, Rs 37,500.
 */

export function formatIndianNumber(num) {
  if (num == null || isNaN(num)) return "0";
  const n = Number(num);
  const neg = n < 0;
  const abs = Math.abs(n);
  // Indian grouping
  const s = abs.toFixed(0);
  const lastThree = s.slice(-3);
  const other = s.slice(0, -3);
  const grouped =
    other === ""
      ? lastThree
      : other.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," + lastThree;
  return (neg ? "-" : "") + grouped;
}

export function formatRupees(num) {
  return "Rs " + formatIndianNumber(num);
}

/**
 * Compact Indian currency: Cr / Lakh / K.
 */
export function formatRupeesCompact(num) {
  if (num == null || isNaN(num)) return "Rs 0";
  const n = Number(num);
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (abs >= 1_00_00_000) {
    const v = n / 1_00_00_000;
    return `${sign}Rs ${Math.abs(v).toFixed(v % 1 === 0 ? 0 : 2)} Cr`;
  }
  if (abs >= 1_00_000) {
    const v = n / 1_00_000;
    return `${sign}Rs ${Math.abs(v).toFixed(v % 1 === 0 ? 0 : 2)} Lakh`;
  }
  if (abs >= 1_000) {
    return `${sign}Rs ${formatIndianNumber(abs)}`;
  }
  return `${sign}Rs ${formatIndianNumber(abs)}`;
}

/**
 * Visual-first compact currency — no "Rs" prefix, shorter suffixes (L / Cr).
 * Use on dashboards, charts, KPI cards where space is tight.  Formal surfaces
 * (PDFs, Bills, Cash Book) should keep `formatRupees` / `formatRupeesCompact`.
 */
export function formatRupeesVisual(num) {
  if (num == null || isNaN(num)) return "0";
  const n = Number(num);
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (abs >= 1_00_00_000) {
    const v = n / 1_00_00_000;
    return `${sign}${Math.abs(v).toFixed(v % 1 === 0 ? 0 : 2)} Cr`;
  }
  if (abs >= 1_00_000) {
    const v = n / 1_00_000;
    return `${sign}${Math.abs(v).toFixed(v % 1 === 0 ? 0 : 2)} L`;
  }
  if (abs >= 1_000) {
    return `${sign}${(abs / 1000).toFixed(abs % 1000 === 0 ? 0 : 1)} K`;
  }
  return `${sign}${formatIndianNumber(abs)}`;
}

export function formatPercent(n, decimals = 2) {
  if (n == null || isNaN(n)) return "0.00%";
  return `${Number(n).toFixed(decimals)}%`;
}

export function formatDate(iso) {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

export function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

export const INVESTMENT_TYPE_LABEL = {
  land: "Land",
  loan: "Hand Loan",
  company_share: "Company Share",
  business_franchise: "Business / Franchise",
  sebi_startup_fund: "SEBI Startup Fund",
  sip: "SIP",
  lumpsum: "Lumpsum",
};
